import {
  Divider,
  makeStyles,
  Spinner,
  tokens,
  Tooltip,
} from "@fluentui/react-components";
import React from "react";
import { Link } from "react-router-dom";
import { HOPLegendContent } from "../../../../components/health-of-operations/HOPLegendContent";
import { LayoutContext } from "../../../../context/LayoutContext";
import {
  $black,
  $cloud10,
  $cloud100,
  $cloud20,
  $cloud30,
  $cloud50,
  $cloud90,
  $sky60,
  $white,
} from "../../../../cssjs/colors";
import HOPEnum from "../../../../enum/HOPEnum";
import { useApiCall } from "../../../../hooks/useApiCall";
import IHOPLegendItem from "../../../../models/health-of-operations/IHOPLegendItem";
import { IHOPSummaryResponse } from "../../../../models/health-of-operations/IHOPSummary";
import { getApiUrl } from "../../../../utils/common/CommonHelperFunctions";
import { EMPTY_STATE_MESSAGE } from "../../../../utils/common/Constants";
import { HOPHelper } from "../../../../utils/health-of-operations/HOPHelper";
import { AmdRadarChart } from "../../../common/charts/RadarChart/RadarChart";
import { StatusIconSelector } from "../../../common/icon-selectors/StatusIconSelector";
import { NoDataCard } from "../../../common/no-data-card/NoDataCard";
import { AmdProgressIndicatorV2 } from "../../../common/progress-indicator/ProgressIndicatorV2";
import { HOPOtpCard } from "./HOPOtpCard";

export interface IHealthOfOperationsCardProps {
  airport: string;
}

export const HealthOfOperationsCard: React.FunctionComponent<
  IHealthOfOperationsCardProps
> = ({ airport }): React.JSX.Element => {
  const { globalFilterDate } = React.useContext(LayoutContext);
  const selectedTime = {
    start: globalFilterDate.startDate,
    end: globalFilterDate.endDate,
  };
  const { timezone } = React.useContext(LayoutContext);

  const getOtpAggregareUrl = getApiUrl(
    process.env.REACT_APP_API_OTP || "",
    airport,
    globalFilterDate.startDate,
    globalFilterDate.endDate,
    timezone
  );

  const getHOPSummaryUrl = getApiUrl(
    process.env.REACT_APP_API_HOPSUMMARY || "",
    airport,
    globalFilterDate.startDate,
    globalFilterDate.endDate,
    timezone
  );
  const {
    data: hopSummaryData,
    error: error,
    isLoading = false,
  } = useApiCall<IHOPSummaryResponse>({
    url: getHOPSummaryUrl,
    dependencies: [airport, selectedTime, globalFilterDate, timezone],
  });

  const {
    data: otpData = [],
    error: otpError,
    isLoading: isOtpLoading,
  } = useApiCall<any[]>({
    url: getOtpAggregareUrl,
    dependencies: [airport, globalFilterDate, timezone],
  });
  const [hopChartData, setHopChartData] = React.useState<any>();
  const [otherPerformance, setOtherPerformance] = React.useState<any>();

  React.useEffect(() => {
    if (hopSummaryData) {
      const { chartData, otherPerformance } = HOPHelper.extractHOPSummaryData(
        hopSummaryData,
        selectedTime,
        airport,
        timezone
      );
      setHopChartData(chartData);
      setOtherPerformance(otherPerformance);
    } else {
      setHopChartData(undefined);
      setOtherPerformance(undefined);
    }
  }, [hopSummaryData, globalFilterDate, airport, timezone]);

  const classes = useStyles();

  const getLegendContent = (legend: IHOPLegendItem) => {
    return (
      <HOPLegendContent
        legend={legend}
        selectedTime={selectedTime}
        airport={airport}
      />
    );
  };

  return (
    <div
      className={`${classes.container}`}
      data-testid="health-of-operations-card"
    >
      <div className="pb-6">
        <div
          className={`fs-18 fw-600 amd-container-vstart ${classes.cardTitle}`}
        >
          Health of operations
        </div>
        <div className="amd-container-hcenter amd-container-vcenter">
          {isLoading ? (
            <Spinner
              labelPosition="below"
              label="Loading..."
              className={classes.cardLoader}
            />
          ) : error ? (
            <NoDataCard message="No data found" />
          ) : (
            <AmdRadarChart chartData={hopChartData} />
          )}
        </div>
      </div>
      <div className={`fs-14 fw-600`}>
        <div className={`mb-4 ${classes.progressIndicator}`}>
          <div>
            <StatusIconSelector status={HOPEnum.HOPDataStatus.Good} />
          </div>
          <div className="mx-1 fw-600">Confidence Factor</div>
          <div data-testid="progressIndicator">
            <AmdProgressIndicatorV2
              value={0.85}
              size="large"
              shape="rounded"
              color={HOPHelper.getConfidenceFactorColor(
                HOPEnum.HOPDataStatus.Good
              )}
            />
          </div>
          <div className="ml-1 fw-600">85%</div>
        </div>
        <HOPOtpCard
          otpError={otpError}
          otpData={otpData}
          isOtpLoading={isOtpLoading}
        />
        <Divider />
        <div className={`${classes.opfContainer} mt-2`}>
          <div
            className={`${classes.cardSubTitlevariant} fs-16 amd-container-hcenter mb-2`}
          >
            Other Performance Factors
          </div>
          {error && <NoDataCard message={EMPTY_STATE_MESSAGE.Data} />}
          {isLoading ? (
            <Spinner
              labelPosition="below"
              label="Loading..."
              className={`${classes.cardLoader}`}
            />
          ) : null}
          {!error && !isLoading ? (
            <div
              className={`amd-container-vcolumn ${classes.cardPerformanceFactorItemContainer}`}
            >
              <div
                className={`d-flex ${classes.cardPerformanceFactorHeaderRow}`}
              >
                <div className="flex-2 fs-14"></div>
                <div className="flex-1">Actual</div>
                <div className="flex-1">Forecasted</div>
              </div>

              {otherPerformance
                ?.filter((resource: any) => resource.resourceName !== "OTP")
                .map((resource: any) => {
                  return (
                    <Tooltip
                      key={resource.key}
                      withArrow
                      positioning="before"
                      content={getLegendContent(resource)}
                      relationship="label"
                    >
                      <Link
                        to={`/${resource.redirectToPage}?kpiType=${resource.key}`}
                        key={resource.key}
                        className={`d-flex text-d-none ${classes.cardPerformanceFactorItem}`}
                      >
                        <div className="flex-2">{resource.resourceName}</div>
                        <div className="flex-1 fs-16">
                          <StatusIconSelector status={resource.actualStatus} />
                          <span>
                            {resource.actual ? `${resource.actual}%` : "N/A"}
                          </span>
                        </div>
                        <div className="flex-1 fs-16">
                          <StatusIconSelector
                            status={resource.forecastedStatus}
                          />
                          <span>
                            {resource.forecasted
                              ? `${resource.forecasted}%`
                              : "N/A"}
                          </span>
                        </div>
                      </Link>
                    </Tooltip>
                  );
                })}
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
};

const useStyles = makeStyles({
  container: {
    display: "grid",
    gridGap: "8px",
    gridAutoFlow: "column",
    borderRadius: "8px",
    boxSizing: "border-box",
    background: $white,
    padding: "8px",
    minHeight: "100%",
    color: $cloud100,
    gridTemplateColumns: "1fr clamp(296px, 30%, 406px)",
  },
  cardTitle: {
    color: $sky60,
    paddingLeft: "0px",
  },
  cardLoader: {
    minHeight: "600px",
  },
  otpContainer: {
    background: $cloud10,
    borderRadius: "8px",
    padding: "8px",
  },
  opfContainer: {
    background: $cloud10,
    borderRadius: "8px",
    padding: "8px 0px",
  },
  otpBox: {
    paddingLeft: "8px",
    marginRight: "4px",
  },
  boxBorderRight: {
    borderRight: `1px solid ${$cloud50}`,
  },
  cardSubTitlevariant: {
    color: $sky60,
  },
  otpStatusContainer: {
    paddingBottom: "4px",
  },
  cardPerformanceFactorItemContainer: {
    color: $cloud90,
  },
  cardPerformanceFactorHeaderRow: {
    background: "transparent",
    padding: "4px",
    marginBottom: "4px",
  },
  cardPerformanceFactorItem: {
    borderRadius: "3px",
    cursor: "pointer",
    color: $black,
    background: $cloud20,
    padding: "4px",
    marginBottom: "4px",
    ":hover": {
      backgroundColor: tokens.colorNeutralBackground1,
      color: $sky60,
      border: `1px solid ${$sky60}`,
    },
  },
  progressIndicator: {
    display: "grid",
    gridAutoFlow: "column",
    gridAutoColumns: "auto auto 1fr auto",
    alignItems: "baseline",
  },
});
